import React, {useEffect, useMemo, useReducer, useState} from "react";
import cloneDeep from "lodash.clonedeep";
import {Modal} from "react-bootstrap";
import {useLocaleContext} from "../../../../context/LocaleContext";
import {blockPropertiesUpdate} from "../../../../../api/cp/pages";
import locale from "../../../../locale";

import { BLOCKS_PROPERTIES } from "../constants";
import { BLOCK_TEMPLATE } from "../pages/models";

// Blocks
import HallEpisodesBlockSettings from "./BlockSettingModalContent/HallEpisodesBlockSettings";
import HallDescriptionBlockSettings from "./BlockSettingModalContent/HallDescriptionBlockSettings";
import BannersBlockSettings from "./BlockSettingModalContent/BannersBlockSettings";
import TextBlockSettings from "./BlockSettingModalContent/TextBlockSettings";
import ButtonsBlockSettings from "./BlockSettingModalContent/ButtonsBlockSettings";
import PosterBlockSettings from "./BlockSettingModalContent/PosterBlockSettings";
import CountdownBlockSettings from "./BlockSettingModalContent/CountdownBlockSettings";
import ActivityRatingBlockSettings from "./BlockSettingModalContent/ActivityRatingBlockSettings";
import PartnersBlockSettings from "./BlockSettingModalContent/PartnersBlockSettings";
import CoverBlockSettings from "./BlockSettingModalContent/CoverBlockSettings";
import QuizzesBlockSettings from "./BlockSettingModalContent/QuizzesBlockSettings";
import CustomWidgetBlockSettings from "./BlockSettingModalContent/CustomWidgetBlockSettings";

const blocks = {
    [BLOCK_TEMPLATE.HALL_EPISODES]: HallEpisodesBlockSettings,
    [BLOCK_TEMPLATE.HALL_DESCRIPTION]: HallDescriptionBlockSettings,
    [BLOCK_TEMPLATE.LEGACY_BANNER]: BannersBlockSettings,
    [BLOCK_TEMPLATE.TEXT_BLOCK]: TextBlockSettings,
    [BLOCK_TEMPLATE.BUTTONS]: ButtonsBlockSettings,
    [BLOCK_TEMPLATE.POSTER]: PosterBlockSettings,
    [BLOCK_TEMPLATE.COUNTDOWN]: CountdownBlockSettings,
    [BLOCK_TEMPLATE.ACTIVITY_RATING]: ActivityRatingBlockSettings,
    [BLOCK_TEMPLATE.PARTNERS]: PartnersBlockSettings,
    [BLOCK_TEMPLATE.COVER]: CoverBlockSettings,
    [BLOCK_TEMPLATE.QUIZZES]: QuizzesBlockSettings,
    [BLOCK_TEMPLATE.CUSTOM_WIDGET]: CustomWidgetBlockSettings,
};

const BlockEditModal = ({
    isModalVisible,
    editableBlock,
    template,
    event,
    streams,
    updateBlock,
    onCloseModal,
}) => {
    const $locale = useLocaleContext();
    const [blockProperties, setBlockProperties] = useState(() => cloneDeep(BLOCKS_PROPERTIES[template] || {}));

    const updateBlockProperties = (properties) => {
        setBlockProperties(blockProperties => ({...blockProperties, ...properties}));
    }

    useEffect(() => {
        setBlockProperties({...editableBlock});
    }, [editableBlock]);

    const PageComponent = useMemo(() => {
        if (!editableBlock) return null;

        return blocks[editableBlock?.template] || null;
    }, [editableBlock]);

    async function handleUpdateSettings(value) {
        const response = await blockPropertiesUpdate(editableBlock?.id, value);
        updateBlockProperties({...blockProperties, ...response});
        updateBlock({...editableBlock, ...blockProperties, ...response})
    }

    async function handleUpdateFile(key, file) {
        const formData = new FormData();
        formData.append(key, file);
        const response = await blockPropertiesUpdate(editableBlock?.id, formData);
        updateBlock(response);
    }

    function handleSaveStreams(streamId, checked) {
        const list = checked
            ? [...(blockProperties?.showInStreams || []), streamId]
            : blockProperties?.showInStreams?.filter((id) => id !== streamId)
        ;

        handleUpdateSettings({showInStreams: list});
    }

    function removeTabIndex(dialog) {
        dialog.removeAttribute('tabindex');
    }

    if (!PageComponent) {
        return null;
    }

    return (
        <Modal
            show={isModalVisible}
            onHide={onCloseModal}
            onEntered={removeTabIndex}
        >
            <Modal.Header closeButton>
                <Modal.Title>{locale(`settings.blocks.${editableBlock?.template}`)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <PageComponent
                    $locale={$locale}
                    block={editableBlock}
                    blockProperties={blockProperties}
                    event={event}
                    streams={streams}
                    onUpdateBlockProperties={updateBlockProperties}
                    onSave={handleUpdateSettings}
                    onSaveFile={handleUpdateFile}
                    onSaveStreams={handleSaveStreams}
                />
            </Modal.Body>
        </Modal>
    );
};

export default BlockEditModal;
